/**
 * AudioTrace — an on-screen record of what the audio session actually did.
 *
 * WHY THIS EXISTS. The iOS wake bug has now survived two fixes that were green
 * in every engine available here, because no engine available here can produce
 * the thing that breaks it: a real audio-session interruption. macOS WebKit can
 * be told to suspend a context, which satisfies the precondition, but it is not
 * a phone losing its session to the lock screen. So the difference between the
 * fixes and the defect has been unobservable, and the only instrument that can
 * close it is the device itself.
 *
 * Tethering Safari's Web Inspector works and is how the 2026-08-12 round was
 * measured, but it needs a cable and a Mac in the same room. This renders the
 * same facts ON the page, so one screenshot answers the question.
 *
 * IT IS OFF UNLESS ASKED FOR, AND REFUSED IN PRODUCTION. `?audioTrace=1` on a
 * dev or staging build arms it; anywhere else this module does nothing at all,
 * installs nothing, and allocates nothing. It OBSERVES — it patches no audio
 * code path and calls into none, so it cannot change the behaviour it is there
 * to describe. Nothing it records is user data: context states, page
 * visibility, and whether a media element is paused.
 */
import { AudioGainBus } from './audio-gain-bus.js';

let AudioTrace = {};

(function () {
    'use strict';

    function requested() {
        try {
            if (window.__MOONFALL_CONFIG?.isProd) return false;
            return new URLSearchParams(window.location.search).get('audioTrace') === '1';
        } catch (_) {
            return false;
        }
    }

    if (!requested()) return;

    const MAX_LINES = 18;
    const lines = [];
    const started = Date.now();
    let panel = null;
    /* Identity, not just state. "The context is running again" and "a DIFFERENT
       context is running" are the two outcomes this whole round is about, and
       they read identically unless the contexts are numbered. */
    const elementIds = new WeakMap();
    let nextElementId = 1;
    let lastContextLine = '';
    let lastMusicLine = '';

    function stamp() {
        const ms = Date.now() - started;
        return `${String(Math.floor(ms / 1000)).padStart(3, ' ')}.${String(ms % 1000).padStart(3, '0')}`;
    }

    function push(text) {
        lines.push(`${stamp()} ${text}`);
        if (lines.length > MAX_LINES) lines.shift();
        render();
    }

    function render() {
        if (!panel) {
            panel = document.createElement('pre');
            panel.id = 'audio-trace';
            panel.style.cssText = [
                'position:fixed', 'left:0', 'top:0', 'z-index:2147483646',
                'margin:0', 'padding:4px 6px', 'max-width:70vw',
                'background:rgba(0,0,0,0.78)', 'color:#9fe8b0',
                'font:9px/1.25 ui-monospace,Menlo,monospace',
                'white-space:pre', 'pointer-events:none', 'border-radius:0 0 4px 0',
            ].join(';');
            (document.body || document.documentElement).appendChild(panel);
        }
        panel.textContent = lines.join('\n');
    }

    function idOf(map, value, next) {
        if (!map.has(value)) map.set(value, next());
        return map.get(value);
    }

    /* The music element is the one the bus claimed for CORS; a sound-effects
       voice never carries that attribute. Duration is the tie-breaker before
       metadata lands — a track is minutes, a cue is about a second.
       The label says `cors`, not `carried`, because that attribute is claimed
       the moment the element is created and says nothing about whether the gain
       node is live yet — carriage waits for a context that can run. `vol=1.00`
       is the tell that the graph has taken the level over; anything else means
       the element is still carrying it itself. */
    const audioElements = [];
    const NativeAudio = window.Audio;
    /* A class rather than a function, so the replacement keeps the constructor
       shape `window.Audio` is declared with — a plain function assigned there is
       a type error, and silencing that with a cast would hide a real difference
       in how `new` behaves. */
    class TracedAudio extends NativeAudio {
        constructor(...args) {
            super(...args);
            /* RECORDED, NOT ANNOUNCED. The sound-effects pool builds two voices
               per warmed cue, so a line each buried the context and page
               transitions this exists to show under twenty-five "audio created"
               lines — measured on a real lobby. The music element surfaces
               through `sample()` instead, which is the only one that matters. */
            audioElements.push(this);
        }
    }
    window.Audio = TracedAudio;

    function musicElement() {
        const carried = audioElements.filter((el) => el && el.crossOrigin === 'anonymous');
        if (carried.length) return carried[carried.length - 1];
        const long = audioElements.filter((el) => Number.isFinite(el?.duration) && el.duration > 20);
        return long.length ? long[long.length - 1] : null;
    }

    function sample() {
        // A real export, not the test-bridge seam: `window.UI` exists only in the
        // bridged bundle, so reading it here would print "ctx none" forever on
        // the shipped build — the only build this is for.
        const ctx = AudioGainBus?.describeContext?.() || { generation: 0, state: null };
        /* The context line changes on state or generation only — the graph's
           clock is deliberately NOT part of it, because this samples four times
           a second and a clock in the line would turn it into a stream that
           buries the transitions this overlay exists to show. The clock rides
           the tap line instead (see graphTime()). */
        const ctxLine = ctx.state
            ? `ctx#${ctx.generation} ${ctx.state}`
            : 'ctx none';
        if (ctxLine !== lastContextLine) {
            lastContextLine = ctxLine;
            push(ctxLine);
        }
        const el = musicElement();
        /* Whether the element's audio goes into the graph, and whether that
           graph can be heard. A routed element reports `playing` into a stopped
           graph exactly as it does into a running one, so the element line on
           its own cannot say whether the player hears anything. */
        const carriage = !el ? ''
            : AudioGainBus?.carriageStalled?.(el) ? ' SILENT-CARRIAGE'
            : AudioGainBus?.carriesElement?.(el) ? ' carried'
            : ' direct';
        const musicLine = el
            ? `music#${idOf(elementIds, el, () => nextElementId++)} `
              + `${el.paused ? 'paused' : 'playing'} vol=${Number(el.volume).toFixed(2)}`
              + `${el.muted ? ' muted' : ''}${el.crossOrigin === 'anonymous' ? ' cors' : ''}${carriage}`
            : 'music none';
        if (musicLine !== lastMusicLine) {
            lastMusicLine = musicLine;
            push(musicLine);
        }
    }

    document.addEventListener('visibilitychange', () => {
        push(`page ${document.visibilityState}`);
        sample();
    });
    window.addEventListener('pagehide', () => push('pagehide'));
    window.addEventListener('pageshow', () => { push('pageshow'); sample(); });
    /* `t=` is the graph's own clock. It only moves while the graph renders, so
       two taps that read the same `t=` on a `running` context are a graph that
       is not actually rendering — the one thing no state can say. */
    function graphTime() {
        const t = AudioGainBus?.describeContext?.()?.currentTime;
        return typeof t === 'number' ? ` t=${t.toFixed(1)}` : '';
    }
    document.addEventListener('pointerdown', () => push(`tap${graphTime()}`), true);

    /* Polled rather than hooked, because hooking would mean calling into the
       code under observation. 250ms is far finer than any of these transitions
       and costs nothing on a build nobody ships. */
    setInterval(sample, 250);
    push('trace armed');

    AudioTrace = { sample };
}());

export { AudioTrace };
